import React, { useEffect, useState } from "react";
import classNames from "classnames";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { FaMoneyBillWave } from "react-icons/fa";
import {
  ChevronDown,
  LogOut,
  Settings,
  Target,
  Workflow,
  Bot,
  Users,
  BarChart3,
  ChevronRight,
  Plus,
  Database,
  Zap,
  DatabaseBackupIcon,
  Moon,
  Sun,
  ArrowLeft,
  Send,
} from "lucide-react";
import useRequest from "../../hook/use-req";
import { NavigationApiResponse, ResourceNode, UserResponse } from "../../types";
import { ClockLoader } from "react-spinners";
import JourneyModal from "../../marketing/journey/new-jouney/journey-modal";
import { BroadCastIcon, ChartIcon, ChatIcon, SettingsIcon, StudioIcon, TaskNavIcon, TemplateIcon, UserGroupIcon } from "../../../assets/icon";
import { useTheme } from "../../../context/theme-context";
import { getStoredOrgRole } from "../../../utils/org-role";
import SuiteSwitcher from "../suite-switcher";


interface SidebarProps {
  open: boolean;
  onToggle: () => void;
}

const Sidebar1: React.FC<SidebarProps> = ({ open, onToggle }) => {
  const token = localStorage.getItem("authToken");
  const navigate = useNavigate();
  const location = useLocation();
  const [, setIsModalOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [resources, setResources] = useState<NavigationApiResponse | null>(null);
  const { isDark: darkMode, toggleTheme } = useTheme();
  const [journeyModalOpen, setJourneyModalOpen] = useState(false);
  const [user, setUser] = useState<UserResponse | null>(null);
  const { makeRequest: getUserData } = useRequest(`/auth/get_userdata`, "GET", {
    Authorization: `Bearer ${token}`,
  });

  // ConvoCRM access — see utils/org-role.ts for the full explanation of why
  // this needs org_role rather than just `role`.
  const role = user?.payload?.role || localStorage.getItem("role") || "";
  const orgRole = user?.payload?.org_role || getStoredOrgRole();


  const { makeRequest: getResourceData, loading } = useRequest(`/auth/resources`, "GET", {
    Authorization: `Bearer ${token}`,
  });


  useEffect(() => {
    const fetchData = async () => {
      const [response] = await getUserData();
      setUser(response);
    };
    fetchData();
  }, []);

  const handleSignOut = async () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("resetOtpCode");
    localStorage.removeItem("userDetails");
    localStorage.removeItem("draftCampaignId");
    localStorage.removeItem("draftAudienceId");
    localStorage.removeItem("role");
    navigate("/");
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) =>
      prev.includes(section) ? prev.filter((s) => s !== section) : [...prev, section]
    );
  };


  const getIconForResource = (resourceName: string) => {
    const iconMap: Record<string, any> = {
      Home: TaskNavIcon,
      Broadcasts: BroadCastIcon,
      Segments: UserGroupIcon,
      Templates: TemplateIcon,
      Analytics: ChartIcon,
      "Bot Studio": StudioIcon,
      Settings: SettingsIcon,
      ConvoAI: ChatIcon,
      Campaign: Target,
      Campaigns: Target,
      AllCampaigns: Target,
      CreateCampaigns: Plus,
      Automations: Workflow,
      Workflows: Workflow,
      Triggers: Zap,
      AI_Agents: Bot,
      AIAgents: Bot,
      Sequences: Workflow,
      Contacts: Users,
      Data: Database,
      Connectors: Database,
      Sources: Database,
      Destinations: Database,
      UseCases: Workflow,
      Audience: Users,
      Overview: BarChart3,
      ChannelPerformance: BarChart3,
      CampaignReports: BarChart3,
      Engagement: BarChart3,
      Wallet: FaMoneyBillWave,
      Team: Users,
      APIKeys: DatabaseBackupIcon,
      AutomationSettings: Settings,
      TransactionalMessages: Send,
      "Transactional Messages": Send,
      Transactional_Messages: Send,
    };
    return iconMap[resourceName] || ChatIcon;
  };

  const getPathForResource = (resourceName: string, parentName?: string) => {
    const pathMap: Record<string, string> = {
      Home: "/cpas-dashboard",
      Broadcasts: "/campaign",
      Segments: "/segments",
      Templates: "/templates",
      Analytics: "/analytics",
      "Bot Studio": "/journey",
      Settings: "/settings",
      "Convo AI": "/convoAi",
      SMS: "/dashboard",
      Whatsapp: "/whatsapp-setup",
      Email: "/email",
      RCS: "/rcs-setup",
      Voice: "/voice-setup",
      Webchat: "/webchat-setup",
      Telegram: "/telegram-setup",
      Campaign: "/campaign",
      Campaigns: "/campaign",
      Reseller: "/customer",
      AllCampaigns: "/campaign",
      CreateCampaigns: "/campaigns/create",
      Workflows: "/journeys",
      Triggers: "/triggers",
      AI_Agents: "/#automation/ai-agents",
      Sequences: "/sequences",
      Contacts: "/contact",
      Sources: "/connectors/sources",
      Destinations: "/connectors/destinations",
      UseCases: "/use-cases",
      Audience: "/audience",
      Overview: "/analytics",
      ChannelPerformance: "/analytics/channels",
      CampaignReports: "/analytics/campaigns",
      Engagement: "/analytics/engagement",
      Wallet: "/wallet",
      Team: "/settings/team",
      APIKeys: "/settings/api",
      AutomationSettings: "/settings/automation",
      TransactionalMessages: "/transactional-messages",
      "Transactional Messages": "/transactional-messages",
      Transactional_Messages: "/transactional-messages",
    };
    return pathMap[resourceName] || "#";
  };

  const transformResourceToNavigation = (
    resources: Record<string, ResourceNode>,
    parentKey?: string
  ): any[] => {
    return Object.entries(resources)
      .filter(([_, permission]) => !permission.allowed || permission.allowed === "view")
      .map(([key, permission]) => {
        const hasChildren = Object.keys(permission.subnodes).length > 0;
        const Icon = getIconForResource(key);
        const item: any = {
          id: key.toLowerCase().replace(/\s+/g, "_"),
          label: key.replace(/_/g, " "),
          icon: Icon,
          path: getPathForResource(key, parentKey),
          expandable: hasChildren,
        };

        if (key === "SetMessagingRates") {
          item.onClick = () => setIsModalOpen(true);
          delete item.path;
        }


        if (hasChildren) {
          item.children = transformResourceToNavigation(permission.subnodes, key);
        }

        return item;
      });
  };

  useEffect(() => {
    const fetchData = async () => {
      const [response] = await getResourceData();
      if (response) {
        setResources(response);
      }
    };
    fetchData();
  }, []);

  // Settings and ConvoAI moved out of the sidebar into the navbar's
  // company-name dropdown (see navbar.tsx) — filtered out here rather than
  // at the source since the nav tree comes from the backend.
  const navigationItems = resources?.payload?.navigation?.subnodes
    ? transformResourceToNavigation(resources.payload.navigation.subnodes).filter(
      (item) => !["quickactions", "settings", "convoai", "convo_ai"].includes(item.id)
    )
    : [];

  const renderNavItem = (item: any, level = 0) => {
    const Icon = item.icon;
    const isExpanded = expandedSections.includes(item.id);
    const isActive =
      location.pathname === item.path ||
      (item.children &&
        item.children.some((child: any) => location.pathname === child.path));

    const hasOnClick = item.onClick && typeof item.onClick === "function";

    const content = (
      <div
        className={classNames(
          "flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all duration-150 group",
          {
            "bg-[#EEF2FF] dark:bg-[#4F46E5]/20 text-[#4F46E5] dark:text-[#818CF8]": isActive,
            "text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-800 dark:hover:text-gray-200": !isActive,
            "ml-3": level > 0,
          }
        )}
      >
        {Icon && (
          <Icon
            size={18}
            className={classNames("flex-shrink-0 transition-colors", {
              "text-[#4F46E5] dark:text-[#818CF8]": isActive,
              "text-[#7B7B7B] font-semibold dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300": !isActive,
            })}
          />
        )}
        {open && (
          <span
            className={classNames("text-sm font-semibold flex-1 truncate", {
              "text-[#4F46E5] dark:text-[#818CF8]": isActive,
              "text-gray-700 dark:text-white group-hover:text-gray-900 dark:group-hover:text-white": !isActive,
            })}
          >
            {item.label}
          </span>
        )}
        {open && item.expandable && (
          <div
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleSection(item.id);
            }}
            className="ml-auto"
          >
            {isExpanded ? (
              <ChevronDown size={14} className="text-[#7B7B7B] font-semibold dark:text-gray-500" />
            ) : (
              <ChevronRight size={14} className="text-[#7B7B7B] font-semibold dark:text-gray-500" />
            )}
          </div>
        )}
      </div>
    );

    return (
      <div key={item.id}>
        {hasOnClick ? (
          <div
            onClick={() => {
              localStorage.removeItem("draftCampaignId");
              localStorage.removeItem("draftAudienceId");
              localStorage.removeItem("audienceId");

              item.onClick?.();
            }}
          >
            {content}
          </div>
        ) : (
          <Link
            to={item.path || "#"}
            onClick={() => {
              localStorage.removeItem("draftCampaignId");
              localStorage.removeItem("draftAudienceId");
              localStorage.removeItem("audienceId");
            }}
          >
            {content}
          </Link>
        )}

        {item.expandable && isExpanded && item.children && open && (
          <div className="mt-1 space-y-0.5">
            {item.children.map((child: any) =>
              renderNavItem(child, level + 1)
            )}
          </div>
        )}
      </div>
    );
  };
  return (
    <div
      className={classNames(
        "flex flex-col h-full border-r transition-all duration-300",
        darkMode ? "bg-[#111827] border-gray-800" : "bg-white border-gray-100"
      )}
    // style={{ width: open ? "220px" : "64px" }}
    >
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
        {!open && (
          <div className="flex flex-col items-center justify-center gap-2 w-7">
            <img className="w-5 h-5" src={user?.payload?.org_logo || "/default-logo.png"} alt="logo" />
            <SuiteSwitcher activeSuite="marketing" open={open} onExpandSidebar={onToggle} role={role} orgRole={orgRole} />
            <button
              className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              onClick={onToggle}
              title={open ? "Collapse" : "Expand"}
            >
              <ArrowLeft
                className={`w-3.5 h-3.5 transition-transform ${!open ? "rotate-180" : ""}`}
              />
            </button>
          </div>
        )}

        {open && (
          <div className="flex items-center gap-1.5">
            <img className="w-5 h-5" src={user?.payload?.org_logo || "/default-logo.png"} alt="logo" />
            <SuiteSwitcher activeSuite="marketing" open={open} onExpandSidebar={onToggle} role={role} orgRole={orgRole} />

            {/* Back arrow */}
            <button
              className="ml-auto flex items-center justify-center w-7 h-7 rounded-md border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              onClick={onToggle}
              title={open ? "Collapse" : "Expand"}
            >
              <ArrowLeft
                className={`w-3.5 h-3.5 transition-transform ${!open ? "rotate-180" : ""}`}
              />
            </button>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto py-3 px-2">
        {loading ? (
          <div className="flex justify-center mt-10">
            <ClockLoader color="#4F46E5" loading={loading} size={32} />
          </div>
        ) : (
          <nav className="space-y-0.5">
            {navigationItems.map((item) => renderNavItem(item))}
          </nav>
        )}
      </div>

      {/* Bottom Section */}
      <div
        className={classNames("border-t px-2 py-3 space-y-1", {
          "border-gray-800": darkMode,
          "border-gray-100": !darkMode,
        })}
      >
        {/* Dark Mode Toggle */}
        <div
          className={classNames(
            "flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all",
            darkMode
              ? "text-[#7B7B7B] font-semibold hover:bg-gray-800"
              : "text-gray-700 hover:bg-gray-100"
          )}
          onClick={toggleTheme}
        >
          {darkMode ? (
            <Sun size={18} className="flex-shrink-0 text-[#7B7B7B] font-semibold" />
          ) : (
            <Moon size={18} className="flex-shrink-0 text-[#7B7B7B] font-semibold" />
          )}
          {open && (
            <>
              <span
                className={classNames("text-sm font-semibold flex-1", {
                  "text-white": darkMode,
                  "text-gray-700": !darkMode,
                })}
              >
                Dark Mode
              </span>
              {/* Toggle switch */}
              <div
                className={classNames(
                  "w-9 h-5 rounded-full transition-colors relative flex-shrink-0",
                  darkMode ? "bg-[#4F46E5]" : "bg-gray-200"
                )}
              >
                <div
                  className={classNames(
                    "absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all",
                    darkMode ? "left-4.5 translate-x-0" : "left-0.5"
                  )}
                  style={{ left: darkMode ? "18px" : "2px" }}
                />
              </div>
            </>
          )}
        </div>

        {/* Log Out */}
        <div
          className={classNames(
            "flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all",
            darkMode
              ? "text-[#7B7B7B] font-semibold hover:bg-gray-800"
              : "text-gray-700 hover:bg-gray-100"
          )}
          onClick={handleSignOut}
        >
          <LogOut size={18} className="flex-shrink-0 text-[#7B7B7B] font-semibold" />
          {open && (
            <span
              className={classNames("text-sm font-semibold", {
                "text-white": darkMode,
                "text-gray-700": !darkMode,
              })}
            >
              Log Out
            </span>
          )}
        </div>
      </div>
      <JourneyModal open={journeyModalOpen} onClose={() => setJourneyModalOpen(false)} />
    </div>
  );
};

export default Sidebar1;